"use client";

import { useState, Fragment } from "react";

import { CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import { toast } from "sonner";
import {
  getMonitoringTypeFromToken,
  MONITORING_BADGE_STYLES,
  MONITORING_TYPES,
  createMonitoringIcon,
  getMonitoringConfig,
  type MonitoringType,
} from "@/constants/monitoring";
import { useGetApiToken } from "@/features/tokens/api/token.queries";
import { Spinner } from "@/components/ui/spinner";

// Use the same shared query parameters as other hooks to prevent duplicate API calls
const SHARED_QUERY_PARAMS = { page: 1, pageSize: 100, activeOnly: true };

export default function TokenContainer() {
  const { data, isLoading, isFetching } = useGetApiToken(
    SHARED_QUERY_PARAMS.page,
    SHARED_QUERY_PARAMS.pageSize,
    SHARED_QUERY_PARAMS.activeOnly,
  );
  const [visibleTokens, setVisibleTokens] = useState<Set<string>>(new Set());

  const toggleTokenVisibility = (tokenId: string) => {
    const newVisible = new Set(visibleTokens);
    if (newVisible.has(tokenId)) {
      newVisible.delete(tokenId);
    } else {
      newVisible.add(tokenId);
    }
    setVisibleTokens(newVisible);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const isBusy = isLoading || isFetching;

  const formatTokenDisplay = (token: string, isVisible: boolean) => {
    if (isVisible) return token;
    return "***********";
  };

  const getEntityTypeFromToken = (token: any): MonitoringType => {
    if (!token) return MONITORING_TYPES.KEYWORD;

    // Priority 1: Check entity_types array (most reliable)
    if (token.entity_types) {
      if (token.entity_types.includes("pulse")) {
        return MONITORING_TYPES.PULSE;
      }
      if (
        token.entity_types.includes("social_media") ||
        token.entity_types.includes("keyword")
      ) {
        return MONITORING_TYPES.KEYWORD;
      }
      if (token.entity_types.includes("influencers")) {
        return MONITORING_TYPES.INFLUENCER;
      }
      if (token.entity_types.includes("quadmedia")) {
        return MONITORING_TYPES.SOURCE;
      }
      // if (
      //   token.entity_types.includes("posts") ||
      //   token.entity_types.includes("post_search")
      // ) {
      //   return MONITORING_TYPES.POST_SEARCH;
      // }
    }

    // Priority 2: Check for premium tokens
    const tokenStr = token.api_key_preview || token.token || "";
    if (tokenStr.toLowerCase().includes("premium")) {
      return MONITORING_TYPES.KEYWORD;
    }

    // Priority 3: Fallback to token prefix detection
    return getMonitoringTypeFromToken(tokenStr);
  };

  // Group tokens by monitoring type
  const groupedTokens =
    data?.api_keys?.reduce(
      (groups: Record<MonitoringType, any[]>, token: any) => {
        const monitoringType = getEntityTypeFromToken(token);
        if (!groups[monitoringType]) groups[monitoringType] = [];
        groups[monitoringType].push({
          ...token,
          token: token.api_key_preview, // Ensure token field exists
          monitoringType,
          isPremium: (token.api_key_preview || "")
            .toLowerCase()
            .includes("premium"),
        });
        return groups;
      },
      {} as Record<MonitoringType, any[]>,
    ) || {};

  return (
    <div className="flex h-full min-h-0 flex-col">
      <CardHeader className="flex-shrink-0 flex-row items-center gap-3 pb-4">
        <CardTitle className="text-lg">API Tokens</CardTitle>
        <Badge variant="source">{data?.api_keys?.length || 0}</Badge>
      </CardHeader>

      {/* Tokens List Section */}
      <div className="relative mt-4 h-full flex-1">
        {isBusy && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
            <Spinner size="md" className="stroke-primary!" />
          </div>
        )}
        {/* Empty State */}
        {!isBusy && (!data?.api_keys || data.api_keys.length === 0) && (
          <div className="flex h-full items-center justify-center text-gray-500">
            <div className="text-center">
              <i className="ri-key-line text-[48px] text-gray-300"></i>
              <p className="mt-2 text-sm font-medium">No API tokens yet</p>
              <p className="text-xs text-gray-400">
                Create your first token to get started
              </p>
            </div>
          </div>
        )}

        {/* Grouped Tokens List */}
        {!isBusy && data?.api_keys && data.api_keys.length > 0 && (
          <div
            className="scrollbar-thin h-full overflow-y-auto pr-0"
            style={{
              // Force hardware acceleration and optimize for scrolling
              transform: "translateZ(0)",
              scrollBehavior: "auto",
              overscrollBehavior: "contain",
            }}
          >
            <div className="space-y-6 pr-2">
              {Object.entries(groupedTokens).map(
                ([monitoringTypeKey, tokens]) => {
                  const monitoringType = monitoringTypeKey as MonitoringType;
                  const config = getMonitoringConfig(monitoringType);
                  const hasAnyPremiumToken = (tokens as any[]).some(
                    (token) => token.isPremium,
                  );

                  return (
                    <Fragment key={monitoringType}>
                      <div className="space-y-[10px]">
                        <div className="flex items-center gap-2">
                          {/* Monitoring Type Badge */}
                          <div className="flex items-center gap-2">
                            {hasAnyPremiumToken ? (
                              <div className="flex h-6 w-6 items-center justify-center rounded-[8px] bg-yellow-500 p-1">
                                <i className="ri-star-line text-xs text-white"></i>
                              </div>
                            ) : (
                              <div
                                className={`flex h-6 w-6 items-center justify-center rounded-[8px] p-[3px] text-[14px] ${MONITORING_BADGE_STYLES[monitoringType]}`}
                              >
                                {createMonitoringIcon(
                                  monitoringType,
                                  "text-sm text-white font-light",
                                  true,
                                )}
                              </div>
                            )}
                            <p className="text-sm font-bold text-neutral-800">
                              {hasAnyPremiumToken
                                ? "Premium API"
                                : config.label}
                            </p>
                          </div>
                        </div>
                        <div className="space-y-[10px]">
                          {(tokens as any[]).map((token: any) => {
                            const tokenId = token.api_key_id;
                            const isVisible = visibleTokens.has(tokenId);
                            return (
                              <div
                                key={tokenId}
                                className="relative flex items-center gap-2"
                              >
                                <div className="h-auto min-h-[45px] w-full rounded-md bg-neutral-50 px-5 py-3 pr-25 text-sm font-medium break-words whitespace-pre-wrap text-neutral-800">
                                  {formatTokenDisplay(token.token, isVisible)}
                                </div>

                                <div className="absolute right-2 flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-neutral-950"
                                    icon
                                    onClick={() =>
                                      toggleTokenVisibility(tokenId)
                                    }
                                  >
                                    {isVisible ? (
                                      <i className="ri-eye-off-line text-[20px] text-neutral-950"></i>
                                    ) : (
                                      <i className="ri-eye-line text-[20px] text-neutral-950"></i>
                                    )}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-neutral-950"
                                    onClick={() => copyToClipboard(token.token)}
                                  >
                                    <i className="ri-file-copy-line text-[20px] text-neutral-950"></i>
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </Fragment>
                  );
                },
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
